// src/features/editor/components/blocks/quizBodySync.ts
import { pickLang } from '@/lib/localizedText'
import type { ContentLang } from '@/features/editor/editorSlice'
import type { QuizAnswer, QuizBlock, QuizBody } from '@/types/blocks'

export const emptyBody = (): QuizBody => ({ question: null, answers: [] })

/** The localized quiz body (never null — falls back to an empty body). */
export const getBody = (block: QuizBlock, lang: ContentLang): QuizBody =>
  (lang === 'ru' ? block.body : block.bodyEn) ?? emptyBody()

/**
 * Тело квиза ДЛЯ ПОКАЗА: пустые тексты подтягиваются из другого слота.
 *
 * Фолбэк пополевой, а не «взять чужое тело целиком»: перевод не меняет состав
 * списков, но заполненность отличается от поля к полю — вопрос уже переведён,
 * варианты ещё нет. `isCorrect` берётся из своего слота: структурные поля
 * бэк держит зеркальными в обоих языках.
 *
 * Формам это НЕЛЬЗЯ — там `getBody`: подставленный текст автор сохранит как
 * перевод и затрёт признак непереведённости.
 */
export const getDisplayBody = (block: QuizBlock, lang: ContentLang, strict = false): QuizBody => {
  const own = getBody(block, lang)
  // Строго (холст и миниатюры редактора): только язык правки, без подстановки.
  if (strict) return own
  const other = getBody(block, lang === 'ru' ? 'en' : 'ru')
  return {
    question: pickLang(own.question, other.question) || null,
    answers: own.answers.length
      ? own.answers.map((a, i) => ({
          ...a,
          text: pickLang(a.text, other.answers[i]?.text) || null,
        }))
      : other.answers.map((a) => ({ ...a })),
  }
}

/** Return a copy of `block` with the localized body's question replaced. */
export const setQuestion = (block: QuizBlock, lang: ContentLang, question: string): QuizBlock => {
  const next = { ...getBody(block, lang), question }
  return lang === 'ru' ? { ...block, body: next } : { ...block, bodyEn: next }
}

/** Return a copy of `block` with the localized answer text at `index` replaced. */
export const setAnswerText = (
  block: QuizBlock,
  lang: ContentLang,
  index: number,
  text: string,
): QuizBlock => {
  const body = getBody(block, lang)
  const answers = body.answers.map((a, i) => (i === index ? { ...a, text } : a))
  const next = { ...body, answers }
  return lang === 'ru' ? { ...block, body: next } : { ...block, bodyEn: next }
}

// Structural answer changes (add / remove / toggle correct) MUST keep body and
// bodyEn in sync — the backend rejects unequal-length answer arrays with 400.
// Only text edits diverge between languages.
//
// These operate INDEX-BY-INDEX on both arrays (splice/append at the same
// position in `body.answers` and `bodyEn.answers`), never by rebuilding one
// language from the other's new shape — rebuilding-by-index against a NEW
// array shifts every translation past the change point (e.g. deleting a
// middle option would silently reassign the surviving translations and lose
// the last one). Splicing the same operation into both arrays keeps each
// answer paired with its own-language translation.

/** Append a new empty answer. The edited language gets `''` (ready to type
 *  into); the other language gets `null` (untranslated, matches how a fresh
 *  answer looks before anyone has typed a translation for it). */
export const addAnswer = (block: QuizBlock, lang: ContentLang): QuizBlock => {
  const ruBody = block.body ?? emptyBody()
  const enBody = block.bodyEn ?? emptyBody()
  const newAnswer = (thisLang: ContentLang): QuizAnswer => ({
    text: thisLang === lang ? '' : null,
    isCorrect: false,
  })
  return {
    ...block,
    body: { ...ruBody, answers: [...ruBody.answers, newAnswer('ru')] },
    bodyEn: { ...enBody, answers: [...enBody.answers, newAnswer('en')] },
  }
}

/** Remove the answer at `index` from both languages, in place — same index,
 *  same splice — so every other answer keeps its own-language partner. */
export const removeAnswerAt = (block: QuizBlock, index: number): QuizBlock => {
  const ruBody = block.body ?? emptyBody()
  const enBody = block.bodyEn ?? emptyBody()
  const without = (answers: QuizAnswer[]): QuizAnswer[] => answers.filter((_, i) => i !== index)
  return {
    ...block,
    body: { ...ruBody, answers: without(ruBody.answers) },
    bodyEn: { ...enBody, answers: without(enBody.answers) },
  }
}

/** Set `isCorrect` at `index` in both languages — the one structural field
 *  that must stay mirrored regardless of which language is being edited.
 *  `exclusive` clears every other answer's flag too (single-correct quizzes). */
export const setCorrectness = (
  block: QuizBlock,
  index: number,
  isCorrect: boolean,
  exclusive: boolean,
): QuizBlock => {
  const ruBody = block.body ?? emptyBody()
  const enBody = block.bodyEn ?? emptyBody()
  const apply = (answers: QuizAnswer[]): QuizAnswer[] =>
    answers.map((a, i) => ({
      ...a,
      isCorrect: i === index ? isCorrect : exclusive ? false : a.isCorrect,
    }))
  return {
    ...block,
    body: { ...ruBody, answers: apply(ruBody.answers) },
    bodyEn: { ...enBody, answers: apply(enBody.answers) },
  }
}
